import React from 'react';

import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { useConfigurationStore } from 'store/configurationsStore';

import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { useScheduleQualityScoreLogic } from 'components/widgets/ScheduleQualityScore/ScheduleQualityScore.logic';
import classes from 'components/widgets/ScheduleQualityScore/ScheduleQualityScore.module.scss';
import { useScheduleQualityScoreProvider } from 'components/widgets/ScheduleQualityScore/ScheduleQualityScore.provider';

export const ScheduleQualityScore = ({
    widgetId,
    localFilters,
    latestVersionId,
    globalFilterData,
    compareVersionId,
    contractId,
    externalComponents = null,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title: propTitle,
    setIsPayloadReady,
}: IComponentProps) => {
    const { componentKey, routeId: id, endpoints, name: configTitle } = useConfigurationStore().scheduleQualityScore;

    const { ui, api } = useScheduleQualityScoreProvider({
        widgetId,
        latestVersionId,
        localFilters,
        globalFilterData,
        compareVersionId,
        componentKey,
        contractId,
        endpoints,
    });

    const { editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        title: propTitle ?? configTitle,
        ui,
        reportWidgetRef,
        apiPayload: api.getScheduleQualityScore.payload,
        widgetEndpoint: api.getScheduleQualityScore.endpoint,
    });

    const { logic } = useScheduleQualityScoreLogic({ api, setIsPayloadReady, widgetViewMode });

    return (
        <WidgetWithTitle
            id={id}
            title={<WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />}
            className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                    <ThreeDotsMenu
                        widgetViewMode={widgetViewMode}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        shareTitle={propTitle ?? configTitle}
                    />
                ),
            ]}
            titleFilters={[
                <ReportingVersionAndPills
                    key={'ReportingVersionAndPills'}
                    selectedProjectAndContract={widgetSelectedProjectAndContract}
                    pills={ui.pills}
                    widgetViewMode={widgetViewMode}
                />,
                <PinboardPills
                    key={'pinboardPills'}
                    isInPinboard={WidgetViewModeUtils.isPinboardView(widgetViewMode)}
                    pills={ui.pills}
                    widgetViewMode={widgetViewMode}
                />,
            ]}
        >
            <div
                className={`${classes.container} ${WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) ? classes.inEditMode : ''}`}
            >
                {api.getScheduleQualityScore.instance.isFetching ? (
                    <OverlayWithSpinner />
                ) : (
                    <div className={`${classes.value} ${classes[logic.valueClassName]}`}>{logic.calculatedValue}</div>
                )}
            </div>
        </WidgetWithTitle>
    );
};
